import { useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';

interface MagicLinkAuthProps {
  onEvent?: (message: string, status: 'info' | 'error' | 'success') => void;
}

export const MagicLinkAuth = ({ onEvent }: MagicLinkAuthProps) => {
  const { signIn } = useSignIn();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'input' | 'sending' | 'sent' | 'error'>('input');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    if (!signIn) {
      setErrorMessage('Authentication not ready. Please refresh the page.');
      return;
    }

    setState('sending');
    setErrorMessage('');
    onEvent?.(`Sending magic link to ${email}`, 'info');
    console.log('[MagicLink] Sending magic link to:', email);

    try {
      // Create sign-in with email
      await signIn.create({
        identifier: email,
      });

      // Prepare magic link
      await signIn.prepareFirstFactor({
        strategy: 'email_link',
        emailAddressId: signIn.supportedFirstFactors.find((factor) => factor.strategy === 'email_link')?.emailAddressId,
        redirectUrl: window.location.href,
      });

      setState('sent');
      onEvent?.('Magic link sent! Check your email.', 'success');
      console.log('[MagicLink] ✅ Magic link sent successfully');
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to send magic link';
      setState('error');
      setErrorMessage(errorMsg);
      onEvent?.(errorMsg, 'error');
      console.error('[MagicLink] ❌ Failed:', errorMsg);
    }
  };

  if (state === 'sent') {
    return (
      <div className='flex items-center justify-center min-h-[calc(100vh-12rem)]'>
        <div className='bg-gray-900 border border-gray-700 rounded-lg p-8 max-w-md w-full'>
          <div className='text-center mb-6'>
            <div className='text-6xl mb-4'>📧</div>
            <h2 className='text-2xl font-bold text-white mb-2'>Check your email</h2>
            <p className='text-gray-400'>
              We sent a magic link to <span className='text-blue-400 font-semibold'>{email}</span>
            </p>
          </div>

          <div className='bg-gray-800 border border-gray-700 rounded-lg p-4 mb-4'>
            <p className='text-sm text-gray-300 mb-2'>Click the link in your email to sign in.</p>
            <p className='text-xs text-gray-500'>The link will expire in 10 minutes.</p>
          </div>

          <button
            onClick={() => {
              setState('input');
              setEmail('');
            }}
            className='w-full py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors border border-gray-600'
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center justify-center min-h-[calc(100vh-12rem)]'>
      <div className='bg-gray-900 border border-gray-700 rounded-lg p-8 max-w-md w-full'>
        <div className='text-center mb-6'>
          <h2 className='text-2xl font-bold text-white mb-2'>Sign in with email</h2>
          <p className='text-gray-400'>Enter your email to receive a magic link</p>
        </div>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <input
              type='email'
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrorMessage('');
              }}
              placeholder='Enter your email'
              disabled={state === 'sending'}
              className='w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed'
              autoFocus
              autoComplete='email'
            />
            {errorMessage && <p className='mt-2 text-sm text-red-400'>{errorMessage}</p>}
          </div>

          <button
            type='submit'
            disabled={state === 'sending' || !email}
            className='w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2'
          >
            {state === 'sending' ? (
              <>
                <div className='h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent' />
                Sending...
              </>
            ) : (
              'Continue with Email'
            )}
          </button>
        </form>

        <div className='mt-6 text-center text-xs text-gray-500'>
          <p>We'll send you a magic link to sign in instantly.</p>
          <p className='mt-1'>No password required.</p>
        </div>
      </div>
    </div>
  );
};
